import {
  BUTTON_RADIUS_OPTIONS,
  BUTTON_SIZE_OPTIONS,
  BUTTON_STYLE_OPTIONS,
  BUTTON_WEIGHT_OPTIONS,
  CARD_RADIUS_OPTIONS,
  CONTAINER_WIDTH_OPTIONS,
  DEFAULT_THEME,
  FONT_OPTIONS,
  HEADING_WEIGHT_OPTIONS,
  RADIUS_OPTIONS,
  SECTION_SPACING_OPTIONS,
  SHADOW_OPTIONS,
  type StorefrontTheme,
  themeToCssVars,
} from '@ctrl/sections/theme';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { SelectField, SwitchField } from '@/components/form-fields';
import { MediaPicker } from '@/components/media-picker';
import { ReadOnlyNotice, SectionCard, SettingsSkeleton } from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { getSite, getSiteSettings, saveSiteSettings, saveTheme } from '@/lib/settings/storefront';

/**
 * Branding: the storefront's whole design system, as fifteen colours and a
 * handful of scales.
 *
 * These tokens are what `themeToCssVars` emits onto `:root`, and every
 * section reads them through ordinary utility classes -- so this screen
 * restyles the entire storefront without any section knowing it happened.
 * The preview below uses the same function, so what is shown is literally
 * what will be served.
 */

const COLOR_FIELDS: Array<{ key: keyof StorefrontTheme['colors']; label: string; hint?: string }> =
  [
    { key: 'background', label: 'Page background' },
    { key: 'foreground', label: 'Body text' },
    { key: 'surface', label: 'Cards' },
    { key: 'primary', label: 'Primary', hint: 'Buttons and links.' },
    { key: 'primaryForeground', label: 'On primary' },
    { key: 'accent', label: 'Accent' },
    { key: 'accentForeground', label: 'On accent' },
    { key: 'muted', label: 'Muted surface' },
    { key: 'mutedForeground', label: 'Muted text' },
    { key: 'border', label: 'Borders' },
    { key: 'headerBackground', label: 'Header background' },
    { key: 'headerForeground', label: 'Header text' },
    { key: 'footerBackground', label: 'Footer background' },
    { key: 'footerForeground', label: 'Footer text' },
    { key: 'sale', label: 'Sale price' },
  ];

const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value.replace(/_/g, ' ') }));

export function BrandingSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('cms.manage');
  const queryClient = useQueryClient();

  const site = useQuery({
    queryKey: ['storefront-site', accountId],
    queryFn: () => getSite(accountId),
  });
  const settings = useQuery({
    queryKey: ['site-settings', accountId],
    queryFn: () => getSiteSettings(accountId),
  });

  const [theme, setTheme] = useState<StorefrontTheme | null>(null);

  // The stored theme is the baseline; a refetch after saving rebases it.
  useEffect(() => {
    if (site.data) setTheme(site.data.theme);
  }, [site.data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!site.data || !theme) throw new Error('No storefront to brand yet.');

      await saveTheme(accountId, site.data.id, theme);
    },
    onSuccess: () => {
      toast.success('Branding saved.');
      queryClient.invalidateQueries({ queryKey: ['storefront-site', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveImage = useMutation({
    mutationFn: (patch: { logo_media_id?: string | null; favicon_media_id?: string | null }) =>
      saveSiteSettings(accountId, patch),
    onSuccess: () => {
      toast.success('Saved.');
      queryClient.invalidateQueries({ queryKey: ['site-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveCart = useMutation({
    mutationFn: (behavior: 'drawer' | 'page') =>
      saveSiteSettings(accountId, {
        checkout_settings: {
          cartBehavior: behavior,
          requireAccount: settings.data?.requireAccount ?? false,
        },
      }),
    onSuccess: () => {
      toast.success('Cart behaviour saved.');
      queryClient.invalidateQueries({ queryKey: ['site-settings', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (site.isLoading || settings.isLoading) return <SettingsSkeleton />;

  if (!site.data || !theme) {
    return (
      <p className="text-muted-foreground rounded-lg border border-dashed p-6 text-sm">
        This account has no storefront yet, so there is nothing to brand.
      </p>
    );
  }

  const set = <K extends keyof StorefrontTheme>(group: K, values: Partial<StorefrontTheme[K]>) =>
    setTheme((current) =>
      current ? { ...current, [group]: { ...current[group], ...values } } : current,
    );

  const dirty = JSON.stringify(theme) !== JSON.stringify(site.data.theme);

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <ThemePreview theme={theme} />

      <fieldset disabled={!canManage} className="flex flex-col gap-6">
        <SectionCard
          title="Colours"
          description="Fifteen values the whole storefront is built from. Every section follows them."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {COLOR_FIELDS.map((field) => (
              <Field key={field.key}>
                <FieldLabel htmlFor={`theme-${field.key}`}>{field.label}</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id={`theme-${field.key}`}
                    type="color"
                    className="h-9 w-14 p-1"
                    value={theme.colors[field.key]}
                    onChange={(event) =>
                      set('colors', { [field.key]: event.target.value } as never)
                    }
                  />
                  <Input
                    aria-label={`${field.label} hex`}
                    className="font-mono"
                    value={theme.colors[field.key]}
                    onChange={(event) =>
                      set('colors', { [field.key]: event.target.value } as never)
                    }
                  />
                </div>
                {field.hint && <FieldDescription>{field.hint}</FieldDescription>}
              </Field>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Typography" description="Two faces, and how headings sit against them.">
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="theme-heading-font"
              label="Heading font"
              value={theme.typography.headingFont}
              options={FONT_OPTIONS.map((font) => ({
                value: font.value,
                label: `${font.label} · ${font.category}`,
              }))}
              onChange={(value) => set('typography', { headingFont: value as never })}
            />
            <SelectField
              id="theme-body-font"
              label="Body font"
              value={theme.typography.bodyFont}
              options={FONT_OPTIONS.map((font) => ({
                value: font.value,
                label: `${font.label} · ${font.category}`,
              }))}
              onChange={(value) => set('typography', { bodyFont: value as never })}
            />
            <SelectField
              id="theme-heading-weight"
              label="Heading weight"
              value={theme.typography.headingWeight}
              options={options(HEADING_WEIGHT_OPTIONS)}
              onChange={(value) => set('typography', { headingWeight: value as never })}
            />
            <Range
              id="theme-base-size"
              label="Base text size"
              value={theme.typography.baseFontSize}
              min={13}
              max={20}
              step={1}
              suffix="px"
              onChange={(value) => set('typography', { baseFontSize: value })}
            />
            <Range
              id="theme-heading-scale"
              label="Heading scale"
              description="How much bigger each heading level is."
              value={theme.typography.headingScale}
              min={1}
              max={1.6}
              step={0.05}
              onChange={(value) => set('typography', { headingScale: value })}
            />
            <Range
              id="theme-heading-tracking"
              label="Heading letter spacing"
              value={theme.typography.headingLetterSpacing}
              min={-0.05}
              max={0.3}
              step={0.01}
              suffix="em"
              onChange={(value) => set('typography', { headingLetterSpacing: value })}
            />
          </div>

          <SwitchField
            id="theme-uppercase"
            label="Uppercase headings"
            checked={theme.typography.uppercaseHeadings}
            onChange={(checked) => set('typography', { uppercaseHeadings: checked })}
          />
        </SectionCard>

        <SectionCard
          title="Shape and buttons"
          description="Corners, borders and how a call to action looks."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="theme-radius"
              label="Corner radius"
              value={theme.shape.radius}
              options={options(RADIUS_OPTIONS)}
              onChange={(value) => set('shape', { radius: value as never })}
            />
            <SelectField
              id="theme-card-radius"
              label="Card radius"
              value={theme.shape.cardRadius}
              options={options(CARD_RADIUS_OPTIONS)}
              onChange={(value) => set('shape', { cardRadius: value as never })}
            />
            <SelectField
              id="theme-shadow"
              label="Shadow"
              value={theme.shape.shadow}
              options={options(SHADOW_OPTIONS)}
              onChange={(value) => set('shape', { shadow: value as never })}
            />
            <Range
              id="theme-border"
              label="Border width"
              value={theme.shape.borderWidth}
              min={0}
              max={3}
              step={1}
              suffix="px"
              onChange={(value) => set('shape', { borderWidth: value })}
            />
            <SelectField
              id="theme-button-style"
              label="Button style"
              value={theme.buttons.style}
              options={options(BUTTON_STYLE_OPTIONS)}
              onChange={(value) => set('buttons', { style: value as never })}
            />
            <SelectField
              id="theme-button-radius"
              label="Button radius"
              value={theme.buttons.radius}
              options={options(BUTTON_RADIUS_OPTIONS)}
              onChange={(value) => set('buttons', { radius: value as never })}
            />
            <SelectField
              id="theme-button-size"
              label="Button size"
              value={theme.buttons.size}
              options={options(BUTTON_SIZE_OPTIONS)}
              onChange={(value) => set('buttons', { size: value as never })}
            />
            <SelectField
              id="theme-button-weight"
              label="Button weight"
              value={theme.buttons.weight}
              options={options(BUTTON_WEIGHT_OPTIONS)}
              onChange={(value) => set('buttons', { weight: value as never })}
            />
          </div>

          <SwitchField
            id="theme-button-uppercase"
            label="Uppercase button labels"
            checked={theme.buttons.uppercase}
            onChange={(checked) => set('buttons', { uppercase: checked })}
          />
        </SectionCard>

        <SectionCard
          title="Layout"
          description="How wide the page runs and how much air sections get."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id="theme-container"
              label="Container width"
              value={theme.layout.containerWidth}
              options={options(CONTAINER_WIDTH_OPTIONS)}
              onChange={(value) => set('layout', { containerWidth: value as never })}
            />
            <SelectField
              id="theme-spacing"
              label="Section spacing"
              value={theme.layout.sectionSpacing}
              options={options(SECTION_SPACING_OPTIONS)}
              onChange={(value) => set('layout', { sectionSpacing: value as never })}
            />
            <Range
              id="theme-gap"
              label="Grid gap"
              value={theme.layout.gridGap}
              min={8}
              max={48}
              step={2}
              suffix="px"
              onChange={(value) => set('layout', { gridGap: value })}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Logo and favicon"
          description="Both come from the media library. The logo replaces the shop's name in the header; the favicon is the tab icon."
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <span className="text-sm font-medium">Logo</span>
              <MediaPicker
                accountId={accountId}
                folder="brands"
                label="Choose a logo"
                value={settings.data?.logo?.url ?? null}
                onChange={(picked) => saveImage.mutate({ logo_media_id: picked?.assetId ?? null })}
              />
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium">Favicon</span>
              <MediaPicker
                accountId={accountId}
                folder="brands"
                label="Choose a favicon"
                value={settings.data?.favicon?.url ?? null}
                onChange={(picked) =>
                  saveImage.mutate({ favicon_media_id: picked?.assetId ?? null })
                }
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Cart behaviour"
          description="Whether adding an item opens a drawer or takes the shopper to the cart page."
        >
          <SelectField
            id="cart-behavior"
            label="On add to cart"
            value={settings.data?.cartBehavior ?? 'drawer'}
            options={[
              { value: 'drawer', label: 'Open the cart drawer' },
              { value: 'page', label: 'Go to the cart page' },
            ]}
            onChange={(value) => saveCart.mutate(value as 'drawer' | 'page')}
          />
        </SectionCard>

        <div className="flex items-center gap-2">
          <Button type="button" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save branding'}
          </Button>
          {dirty && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setTheme(site.data?.theme ?? null)}
            >
              Discard
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="ml-auto"
            onClick={() => setTheme(DEFAULT_THEME)}
          >
            Reset to the shipped design
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

/**
 * The tokens applied to a scrap of the storefront.
 *
 * Rendered with the same `themeToCssVars` the storefront's root layout uses,
 * so this is the design itself rather than an approximation of it.
 */
function ThemePreview({ theme }: { theme: StorefrontTheme }) {
  const vars = themeToCssVars(theme) as unknown as React.CSSProperties;

  return (
    <div className="overflow-hidden rounded-lg border">
      <div style={vars}>
        <div
          className="flex items-center justify-between px-5 py-3 text-sm"
          style={{
            background: 'var(--sf-header-bg)',
            color: 'var(--sf-header-fg)',
            fontFamily: 'var(--sf-font-body)',
          }}
        >
          <span style={{ fontFamily: 'var(--sf-font-heading)', fontWeight: 600 }}>Your store</span>
          <span>Cart (2)</span>
        </div>

        <div
          className="flex flex-col gap-4 p-6"
          style={{ background: 'var(--sf-background)', color: 'var(--sf-foreground)' }}
        >
          <p
            className="text-xs uppercase"
            style={{
              letterSpacing: 'var(--sf-eyebrow-tracking)',
              color: 'var(--sf-muted-foreground)',
            }}
          >
            New this week
          </p>

          <h3
            style={{
              fontFamily: 'var(--sf-font-heading)',
              fontSize: 'var(--sf-h3)',
              fontWeight: Number(theme.typography.headingWeight),
              letterSpacing: 'var(--sf-heading-tracking)',
              textTransform: theme.typography.uppercaseHeadings ? 'uppercase' : 'none',
            }}
          >
            Sun-grown flower, picked this morning
          </h3>

          <div className="flex items-center gap-3">
            <div
              className="flex-1 p-4"
              style={{
                background: 'var(--sf-surface)',
                border: `var(--sf-border-width) solid var(--sf-border)`,
                borderRadius: 'var(--sf-card-radius)',
                boxShadow: 'var(--sf-shadow)',
              }}
            >
              <p className="text-sm" style={{ fontFamily: 'var(--sf-font-body)' }}>
                Gumosa · 7g
              </p>
              <p className="text-sm font-semibold">$89.99</p>
            </div>

            <button
              type="button"
              className="text-sm"
              style={{
                background: 'var(--sf-primary)',
                color: 'var(--sf-primary-foreground)',
                height: 'var(--sf-button-height)',
                padding: 'var(--sf-button-padding)',
                borderRadius: 'var(--sf-button-radius)',
                fontWeight: Number(theme.buttons.weight),
                textTransform: theme.buttons.uppercase ? 'uppercase' : 'none',
                letterSpacing: 'var(--sf-button-tracking)',
              }}
            >
              Add to cart
            </button>
          </div>
        </div>

        <div
          className="px-5 py-3 text-xs"
          style={{ background: 'var(--sf-footer-bg)', color: 'var(--sf-footer-fg)' }}
        >
          Adults 21+ only. Keep out of reach of children.
        </div>
      </div>
    </div>
  );
}

function Range({
  id,
  label,
  description,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        <span className="text-muted-foreground ml-2 font-normal">
          {value}
          {suffix ?? ''}
        </span>
      </FieldLabel>
      <Input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  );
}
